import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, LogOut } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '@/stores/auth';

export default function SoleilPublicLayout() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/courses', label: t('nav.courses') },
    { href: '/exams', label: t('nav.exams') },
    { href: '/about', label: t('nav.about') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const isActive = (href: string) => location.pathname === href;

  const S = {
    font: "'Sora', system-ui, sans-serif",
    heading: "'Fraunces', Georgia, serif",
  };

  return (
    <div className="soleil min-h-screen flex flex-col" style={{ fontFamily: S.font }}>

      {/* ═══ V9v2 NAVBAR ═══ */}
      <header
        className="sticky top-0 z-50 w-full transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(60,54,50,0.97)' : 'var(--soleil-dark, #3C3632)',
          backdropFilter: scrolled ? 'blur(14px) saturate(1.4)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent',
        }}
      >
        <div className="container flex h-16 items-center justify-between px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            </div>
            <span style={{ fontFamily: S.heading, fontSize: 18, fontWeight: 600, color: '#fff' }}>
              ITIL Shift
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="relative text-sm transition-colors rounded-lg"
                style={{
                  fontFamily: S.font,
                  color: isActive(link.href) ? 'var(--soleil-dark)' : 'rgba(255,255,255,0.7)',
                  fontWeight: isActive(link.href) ? 600 : 500,
                  background: isActive(link.href) ? 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))' : 'none',
                  padding: isActive(link.href) ? '6px 16px' : '6px 0',
                  borderRadius: 8,
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          {/* Actions */}
          <div className="flex items-center gap-2">
            <button onClick={toggleLanguage} className="hidden md:flex px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors hover:opacity-80" style={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.25)', fontFamily: S.font }}>
              {i18n.language === 'en' ? 'العربية' : 'English'}
            </button>

            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-2">
                <Link
                  to="/dashboard"
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-colors hover:opacity-80"
                  style={{ color: 'rgba(255,255,255,0.7)', fontFamily: S.font }}
                >
                  {t('nav.dashboard')}
                </Link>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border"
                    style={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.25)', fontFamily: S.font }}
                  >
                    {t('nav.admin')}
                  </Link>
                )}
                <button onClick={handleLogout} className="p-2 rounded-lg hover:opacity-80" style={{ color: 'var(--soleil-mut)' }}>
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors hover:opacity-80"
                  style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', fontFamily: S.font }}
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2 text-sm font-semibold rounded-lg transition-colors hover:opacity-90"
                  style={{
                    color: 'var(--soleil-dark)', fontFamily: S.font,
                    background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
                    boxShadow: '0 2px 10px rgba(184,148,58,0.25)',
                  }}
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}

            <button
              className="md:hidden p-2 rounded-lg"
              style={{ color: '#fff' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', background: 'var(--soleil-dark)' }}>
            <nav className="container py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block py-2.5 px-3 text-sm font-medium rounded-lg"
                  style={{
                    color: isActive(link.href) ? 'var(--soleil-gold-light)' : 'rgba(255,255,255,0.7)',
                    background: isActive(link.href) ? 'rgba(184,148,58,0.15)' : 'transparent',
                    fontFamily: S.font,
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 mt-2 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="block w-full text-center py-2.5 text-sm font-semibold rounded-lg border"
                      style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', fontFamily: S.font }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.dashboard')}
                    </Link>
                    <button
                      className="block w-full text-center py-2.5 text-sm font-medium rounded-lg"
                      style={{ color: 'rgba(255,255,255,0.6)', fontFamily: S.font }}
                      onClick={handleLogout}
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block w-full text-center py-2.5 text-sm font-semibold rounded-lg border"
                      style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', fontFamily: S.font }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="block w-full text-center py-2.5 text-sm font-semibold rounded-lg"
                      style={{
                        color: 'var(--soleil-dark)', fontFamily: S.font,
                        background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
                      }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.register')}
                    </Link>
                  </>
                )}
              </div>
              <button onClick={() => { toggleLanguage(); setMobileMenuOpen(false); }} className="block w-full text-center py-2.5 text-sm font-medium rounded-lg mt-2" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: S.font, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* ═══ MAIN CONTENT ═══ */}
      <main className="flex-1" style={{ background: 'var(--soleil-cream)' }}>
        <Outlet />
      </main>

      {/* ═══ V9v2 FOOTER ═══ */}
      <footer style={{ borderTop: 'none', background: 'var(--soleil-dark, #2E2A26)', padding: '24px 0 16px' }}>
        <div className="container px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              </div>
              <span style={{ fontFamily: S.heading, fontSize: 16, fontWeight: 600, color: '#fff' }}>
                ITIL Shift
              </span>
              <span style={{ fontFamily: S.font, fontSize: 10, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em' }}>
                Certification Academy
              </span>
            </div>
            <div className="flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-xs font-medium transition-colors hover:opacity-80"
                  style={{ color: 'rgba(255,255,255,0.6)', fontFamily: S.font }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <span style={{ fontFamily: S.font, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              © {new Date().getFullYear()} · Baghdad, Iraq
            </span>
          </div>

          {/* Legal & Accreditation */}
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
            <p style={{ fontFamily: S.font, fontSize: 9, color: 'rgba(255,255,255,0.35)', lineHeight: 2, maxWidth: 800, margin: '0 auto' }}>
              Training provided in partnership with{' '}
              <a href="https://www.peoplecert.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--soleil-gold-light)', textDecoration: 'none' }}>
                Raj Khanna Associates Limited
              </a>
              , a UK-based Accredited Training Organisation of PeopleCert.
              <br/>
              ITIL<sup>®</sup> is a registered trade mark of AXELOS Limited, used under permission of AXELOS Limited.
              <br/>
              The Swirl logo™ is a trade mark of AXELOS Limited, used under permission of AXELOS Limited.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
